import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSecurity } from '../../context/SecurityContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { FormInput, PasswordInput, EmailInput } from '../../components/ui/form-input';
import { useToast } from '../../hooks/use-toast';
import { Key, ArrowLeft, AlertCircle, X, Shield, UserPlus } from 'lucide-react';

// Google Icon SVG
const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const AdminLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user } = useAuth();
  const { setTokensAfterLogin } = useSecurity();
  const { branding } = useTheme();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassKey, setShowPassKey] = useState(false);
  const [showRequestAccess, setShowRequestAccess] = useState(false);
  const [passKey, setPassKey] = useState('');
  const [loadingPassKey, setLoadingPassKey] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [shakeFields, setShakeFields] = useState({ email: false, password: false });

  // Redirect if already logged in as admin
  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'super_admin')) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Handle OAuth errors from redirect
  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      const errorMessages = {
        'oauth_denied': 'Google sign-in was cancelled',
        'missing_params': 'Authentication parameters missing',
        'invalid_state': 'Invalid authentication session',
        'state_expired': 'Authentication session expired. Please try again.',
        'token_exchange_failed': 'Failed to complete authentication',
        'userinfo_failed': 'Failed to get user information',
        'oauth_error': 'An error occurred during authentication',
        'no_email': 'No email address found in Google account',
        'account_inactive': 'Your account is not active. Please contact support.',
        'auth_failed': 'Authentication failed. Please try again.',
        'not_admin': 'This account does not have admin privileges.'
      };
      
      toast({
        title: 'Sign-in Error',
        description: errorMessages[error] || 'An error occurred during sign-in',
        variant: 'destructive'
      });
    }
  }, [searchParams, toast]);

  // Google OAuth handler for Admin — backend derives the post-auth redirect
  // from the request's Origin header (works across preview / production /
  // custom domains).
  const handleGoogleSignIn = () => {
    window.location.href = `${window.location.origin}/api/auth/google?role=admin`;
  };

  const triggerShake = (field) => {
    setShakeFields(prev => ({ ...prev, [field]: true }));
    setTimeout(() => {
      setShakeFields(prev => ({ ...prev, [field]: false }));
    }, 500);
  };

  const handleLogin = async () => {
    // Client-side validation with shake animation
    if (!email) {
      setLoginError({ type: 'email', message: 'Please enter your email address' });
      triggerShake('email');
      return;
    }
    
    if (!password) {
      setLoginError({ type: 'password', message: 'Please enter your password' });
      triggerShake('password');
      return;
    }

    setLoginError(null);
    setLoadingLogin(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/auth/login`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onload = async () => {
      setLoadingLogin(false);
      
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          
          // Verify user is admin
          if (data.user.role !== 'admin' && data.user.role !== 'super_admin') {
            setLoginError({
              type: 'account',
              message: 'This account does not have admin privileges. Please use the vendor login.'
            });
            return;
          }
          
          // Store refresh_token in BOTH sessionStorage AND localStorage —
          // localStorage acts as cross-tab passport so newly opened tabs can
          // hydrate. Backend also sets HttpOnly cookie for defence in depth.
          if (data.refresh_token) {
            sessionStorage.setItem('refresh_token', data.refresh_token);
            try { localStorage.setItem('refresh_token', data.refresh_token); } catch (_) { /* quota */ }
          }

          if (data.must_change_password) {
            sessionStorage.setItem('temp_token', data.access_token);
            sessionStorage.setItem('temp_user', JSON.stringify(data.user));
            
            toast({
              title: '🔐 Password Change Required',
              description: 'Please change your temporary password to continue.',
            });
            
            navigate('/change-password?required=true');
            return;
          }
          
          await login(data.user, data.access_token);
          // Activate SecurityContext's proactive token refresh & idle tracking
          setTokensAfterLogin(
            data.access_token,
            data.expires_in_seconds || 3600,
            data.security_settings
          );
          
          toast({
            title: '✅ Success',
            description: `Welcome back, ${data.user.name}!`,
          });
          
          navigate('/dashboard');
        } catch (parseError) {
          console.error('Error parsing success response:', parseError);
          toast({
            title: '❌ Login Failed',
            description: 'An unexpected error occurred. Please try again.',
            variant: 'destructive'
          });
        }
      } else {
        try {
          const errorData = JSON.parse(xhr.responseText);
          const errorCode = errorData.detail || 'Login failed';
          
          if (errorCode === 'email_not_found') {
            setLoginError({
              type: 'email',
              message: 'No admin account found with this email address'
            });
            triggerShake('email');
          } else if (errorCode === 'incorrect_password') {
            setLoginError({
              type: 'password',
              message: 'Incorrect password. Please try again.'
            });
            triggerShake('password');
          } else if (errorCode.toLowerCase().includes('pending') || errorCode.toLowerCase().includes('inactive') || errorCode.toLowerCase().includes('approval')) {
            setLoginError({
              type: 'account',
              message: errorCode
            });
          } else {
            toast({
              title: '❌ Login Failed',
              description: errorCode,
              variant: 'destructive'
            });
          }
        } catch (parseError) {
          toast({
            title: '❌ Login Failed',
            description: 'An unexpected error occurred. Please try again.',
            variant: 'destructive'
          });
        }
      }
    };
    
    xhr.onerror = () => {
      setLoadingLogin(false);
      toast({
        title: '❌ Login Failed',
        description: 'Unable to connect to the server. Please check your internet connection.',
        variant: 'destructive'
      });
    };
    
    xhr.send(JSON.stringify({ email, password, role: 'admin' }));
  };

  const handlePassKeyLogin = async (e) => {
    e.preventDefault();
    
    if (!passKey || passKey.trim() === '') {
      toast({
        title: '❌ Error',
        description: 'Please enter a pass key',
        variant: 'destructive'
      });
      return;
    }

    setLoadingPassKey(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

      const response = await fetch(`${BACKEND_URL}/api/team/pass-keys/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pass_key: passKey.trim().toUpperCase()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid pass key');
      }
      
      const tempUser = {
        id: 'temp_' + Date.now(),
        name: `Temporary: ${data.role_name}`,
        email: 'temporary@admin.access',
        role: 'admin',  // Always set to admin for passkey access
        admin_type: 'temporary',
        is_temporary: true,
        permissions: data.permissions,
        passkey_role: data.role_name,
        expires_at: data.expires_at
      };
      
      await login(tempUser, data.access_token);
      setTokensAfterLogin(data.access_token, data.expires_in_seconds || 3600);
      
      toast({
        title: '✅ Temporary Access Granted',
        description: `You have ${data.role_name} access until ${new Date(data.expires_at).toLocaleString()}`,
      });
      
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: '❌ Access Denied',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoadingPassKey(false);
    }
  };

  // Request Access Form
  const renderRequestAccessForm = () => (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowRequestAccess(false)}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Login
      </Button>

      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-3">
          <UserPlus className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Request Admin Access</h3>
        <p className="text-sm text-gray-600">
          Contact your organization&apos;s Super Admin to request admin access
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
        <h4 className="font-medium text-blue-900">How to get admin access:</h4>
        <ol className="list-decimal list-inside text-sm text-blue-800 space-y-2">
          <li>Contact your organization&apos;s Super Admin</li>
          <li>Request to be added as a team member with admin role</li>
          <li>You will receive an invitation email with login credentials</li>
          <li>Use the temporary password to login and set your own password</li>
        </ol>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-sm text-gray-600">
          <strong>Already have a pass key?</strong> Use the temporary access option to login immediately.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={() => { setShowRequestAccess(false); setShowPassKey(true); }}
        >
          <Key className="h-4 w-4 mr-2" />
          Enter Pass Key
        </Button>
      </div>
    </div>
  );

  // Pass Key Login Form
  const renderPassKeyForm = () => (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowPassKey(false)}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Login
      </Button>

      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-3">
          <Key className="h-8 w-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Temporary Admin Access</h3>
        <p className="text-sm text-gray-600">
          Enter your temporary pass key to access the admin portal
        </p>
      </div>

      <form onSubmit={handlePassKeyLogin} className="space-y-4">
        <FormInput
          id="passkey"
          label="Pass Key"
          type="text"
          placeholder="TPK-XXXX-XXXX"
          value={passKey}
          onChange={(e) => setPassKey(e.target.value.toUpperCase())}
          className="text-center"
          inputClassName="text-lg font-mono tracking-wider text-center"
          maxLength={13}
          required
          hint="Format: TPK-XXXX-XXXX"
        />

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-xs text-yellow-800">
            <strong>Note:</strong> Pass keys allow temporary access and will expire after the specified duration.
            Contact your Super Admin if you need a new pass key.
          </p>
        </div>

        <Button 
          type="submit" 
          className="w-full" 
          size="lg"
          disabled={loadingPassKey}
        >
          {loadingPassKey ? 'Verifying...' : 'Access Admin Portal'}
        </Button>
      </form>
    </div>
  );

  // Main Login Form
  const renderLoginForm = () => (
    <>
      {/* Google Sign-In Button */}
      <Button
        type="button"
        variant="outline"
        className="w-full mb-4 h-11 border-gray-300 hover:bg-gray-50"
        onClick={handleGoogleSignIn}
        data-testid="admin-google-signin"
      >
        <GoogleIcon />
        Continue with Google
      </Button>
      
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-gray-500">Or continue with email</span>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} className="space-y-4">
        {/* Account Error Alert */}
        {loginError && loginError.type === 'account' && (
          <div className="flex items-start gap-2 p-3 rounded-lg border bg-amber-50 border-amber-200 text-amber-800 animate-fadeIn">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium">Account Issue</p>
              <p className="text-sm mt-0.5">{loginError.message}</p>
            </div>
            <button 
              type="button"
              onClick={() => setLoginError(null)}
              className="flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        
        {/* Email Input */}
        <EmailInput
          id="admin-email"
          label="Admin Email"
          placeholder="admin@marketplace.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setLoginError(null); }}
          error={loginError?.type === 'email' ? loginError.message : undefined}
          shake={shakeFields.email}
          required
          showRequiredHint={false}
        />
        
        {/* Password Input */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="admin-password" className="text-sm font-medium">
              Password <span className="text-red-500">*</span>
            </Label>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-blue-600 hover:underline"
            >
              Forgot Password?
            </button>
          </div>
          <PasswordInput
            id="admin-password"
            label=""
            placeholder="Enter password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setLoginError(null); }}
            error={loginError?.type === 'password' ? loginError.message : undefined}
            shake={shakeFields.password}
            required
            showRequiredHint={false}
            hint=""
          />
        </div>
        
        <Button 
          type="submit" 
          className="w-full bg-slate-800 hover:bg-slate-900 text-white" 
          size="lg" 
          disabled={loadingLogin}
          data-testid="admin-login-submit"
        >
          {loadingLogin ? 'Signing in...' : 'Login to Admin Portal'}
        </Button>
      </form>

      {/* Temporary Access & Request Access Links */}
      <div className="mt-6 space-y-3">
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setShowPassKey(true)}
          data-testid="admin-passkey-btn"
        >
          <Key className="h-4 w-4 mr-2" />
          Temporary Access / Enter Pass Key
        </Button>
        
        <Button
          variant="ghost"
          className="w-full text-gray-600"
          onClick={() => setShowRequestAccess(true)}
          data-testid="admin-request-access-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Request Admin Access
        </Button>
      </div>

      {/* Vendor Login Link */}
      <div className="mt-6 text-center border-t pt-6">
        <p className="text-sm text-gray-600">
          Looking to sell on the marketplace?{' '}
          <Link 
            to="/login" 
            className="font-semibold text-blue-600 hover:text-blue-700 hover:underline"
          >
            Vendor Login
          </Link>
        </p>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <Card className="w-full max-w-md shadow-2xl border-slate-700 bg-white">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-2">
            {branding?.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt="Logo" 
                className="w-16 h-16 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className={`p-3 bg-slate-100 rounded-full ${branding?.logo_url ? 'hidden' : ''}`}>
              <Shield className="h-8 w-8 text-slate-700" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            {branding?.portal_title || 'Admin Portal'}
          </CardTitle>
          <CardDescription className="text-center">
            Secure access for marketplace administrators
          </CardDescription>
        </CardHeader>
        <CardContent>
          {showRequestAccess ? renderRequestAccessForm() : 
           showPassKey ? renderPassKeyForm() : 
           renderLoginForm()}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
